import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { supabase } from '../../../lib/supabaseClient';

// Matches the live applications_status_check CHECK constraint.
const APPLICATION_STATUSES = [
  'submitted', 'pending_verification', 'reviewing', 'shortlisted', 'interviewing',
  'offered', 'accepted', 'rejected', 'unsuitable', 'withdrawn',
];

const formatStatusLabel = (status) =>
  status.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/**
 * Per-status cooldown rules for how often a candidate may request an update
 * on their own application. A status with no row here is disabled by
 * default (see create_candidate_request_settings.sql) - this page always
 * shows all statuses, defaulting anything not yet configured to Disabled.
 */
const CandidateRequestSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [rules, setRules] = useState(() =>
    Object.fromEntries(APPLICATION_STATUSES.map((s) => [s, { mode: 'disabled', cooldown_days: 14 }]))
  );

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('candidate_request_settings')
        .select('application_status, mode, cooldown_days')
        .eq('user_id', user.id);

      if (fetchError) throw fetchError;

      setRules((prev) => {
        const next = { ...prev };
        (data || []).forEach((row) => {
          next[row.application_status] = {
            mode: row.mode,
            cooldown_days: row.cooldown_days || 14,
          };
        });
        return next;
      });
    } catch (err) {
      console.error('Error fetching candidate request settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateRule = (status, patch) => {
    setRules((prev) => ({ ...prev, [status]: { ...prev[status], ...patch } }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const rows = APPLICATION_STATUSES.map((status) => ({
        user_id: user.id,
        application_status: status,
        mode: rules[status].mode,
        cooldown_days: rules[status].mode === 'interval' ? rules[status].cooldown_days : null,
      }));

      const { error: upsertError } = await supabase
        .from('candidate_request_settings')
        .upsert(rows, { onConflict: 'user_id,application_status' });

      if (upsertError) throw upsertError;

      setSuccess('Candidate request settings saved');
    } catch (err) {
      console.error('Error saving candidate request settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Candidate Requests</h2>
        <p className="text-muted-foreground">
          Control how often a candidate may request an update on their own application, per status.
          A status left as Disabled shows no request option to the candidate at all.
        </p>
      </div>

      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="h-5 w-5 mr-2 text-indigo-600" />
            Request Cooldowns
          </CardTitle>
          <CardDescription>Feedback requests (a separate request type) are always allowed once per application when a status is rejected/unsuitable and no feedback has been given yet - this grid only governs general "request an update" requests.</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="divide-y">
            {APPLICATION_STATUSES.map((status) => {
              const rule = rules[status];
              return (
                <div key={status} className="flex flex-wrap items-center gap-3 py-3">
                  <div className="w-40 shrink-0 font-medium text-sm">{formatStatusLabel(status)}</div>
                  <select
                    value={rule.mode}
                    onChange={(e) => updateRule(status, { mode: e.target.value })}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option value="disabled">Disabled</option>
                    <option value="once">Once only</option>
                    <option value="interval">Every N days</option>
                  </select>
                  {rule.mode === 'interval' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        value={rule.cooldown_days}
                        onChange={(e) => updateRule(status, { cooldown_days: parseInt(e.target.value, 10) || 1 })}
                        className="w-20 h-9 rounded-md border border-input bg-background px-2 text-sm"
                      />
                      <span className="text-sm text-muted-foreground">days</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default CandidateRequestSettings;
